import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { FiExternalLink, FiRepeat, FiBookmark, FiBriefcase, FiArrowRight } from "react-icons/fi";
import companies from "../../data/companies";
import { collectRevise, todayKey } from "../../utils/revision";

const SHOW = 5; // max rows per follow-up group

function daysSince(dateLike) {
  if (!dateLike) return null;
  const d = new Date(dateLike);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

export default function FollowUps({ questionData, applyData }) {
  const today = todayKey();

  // 1) problems due for revision
  const due = useMemo(() => collectRevise(questionData, today).due, [questionData, today]);

  // 2) stale bookmarks — bookmarked but still unsolved
  const staleBookmarks = useMemo(() => {
    const out = [];
    (questionData || []).forEach((t) =>
      (t.questions || []).forEach((q) => {
        if (q.Bookmark && !q.Done) out.push({ topic: t.topicName, problem: q.Problem, url: q.URL || "" });
      })
    );
    return out;
  }, [questionData]);

  // 3) application follow-ups — applied 7+ days ago, no reply logged
  const appFollowups = useMemo(() => {
    const tracked = (applyData && applyData.tracked) || {};
    const custom = (applyData && Array.isArray(applyData.custom)) ? applyData.custom : [];
    const urlByName = new Map();
    companies.forEach((c) => urlByName.set(c.name.toLowerCase(), c.url));
    custom.forEach((c) => c && c.name && urlByName.set(c.name.toLowerCase(), c.url || ""));

    const out = [];
    Object.keys(tracked).forEach((name) => {
      const t = tracked[name] || {};
      if (!t.applied) return;
      const age = daysSince(t.lastApplied);
      if (age === null || age < 7) return;
      out.push({ name, age, url: urlByName.get(name.toLowerCase()) || "" });
    });
    out.sort((a, b) => b.age - a.age);
    return out;
  }, [applyData]);

  const nothing = due.length === 0 && staleBookmarks.length === 0 && appFollowups.length === 0;

  return (
    <div className="dash-section fu-section">
      <h5>Follow-ups</h5>

      {nothing ? (
        <div className="fu-empty">Nothing needs a nudge right now. You’re on top of it. ✨</div>
      ) : (
        <div className="fu-groups">
          {due.length ? (
            <div className="fu-group">
              <div className="fu-group-head">
                <span className="fu-group-title"><FiRepeat size={14} /> Due for revision</span>
                <span className="fu-count">{due.length}</span>
              </div>
              <ul className="fu-list">
                {due.slice(0, SHOW).map((item) => (
                  <li className="fu-row" key={`${item.ti}-${item.pi}`}>
                    <span className="fu-name" title={item.q.Problem}>{item.q.Problem}</span>
                    <span className="fu-meta">{item.topicName}</span>
                  </li>
                ))}
              </ul>
              <Link to="/revise" className="fu-more">
                Review now <FiArrowRight size={13} />
              </Link>
            </div>
          ) : null}

          {staleBookmarks.length ? (
            <div className="fu-group">
              <div className="fu-group-head">
                <span className="fu-group-title"><FiBookmark size={14} /> Bookmarked, still unsolved</span>
                <span className="fu-count">{staleBookmarks.length}</span>
              </div>
              <ul className="fu-list">
                {staleBookmarks.slice(0, SHOW).map((item, i) => (
                  <li className="fu-row" key={`${item.problem}-${i}`}>
                    <span className="fu-name" title={item.problem}>{item.problem}</span>
                    <span className="fu-meta">{item.topic}</span>
                    {item.url ? (
                      <a className="fu-link" href={item.url} target="_blank" rel="noopener noreferrer" aria-label="Open problem">
                        <FiExternalLink size={13} />
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
              {staleBookmarks.length > SHOW ? (
                <div className="fu-more-note">+{staleBookmarks.length - SHOW} more</div>
              ) : null}
            </div>
          ) : null}

          {appFollowups.length ? (
            <div className="fu-group">
              <div className="fu-group-head">
                <span className="fu-group-title"><FiBriefcase size={14} /> Applications to follow up</span>
                <span className="fu-count">{appFollowups.length}</span>
              </div>
              <ul className="fu-list">
                {appFollowups.slice(0, SHOW).map((item) => (
                  <li className="fu-row" key={item.name}>
                    <span className="fu-name" title={item.name}>{item.name}</span>
                    <span className="fu-meta">{item.age}d ago</span>
                    {item.url ? (
                      <a className="fu-link" href={item.url} target="_blank" rel="noopener noreferrer" aria-label="Open careers page">
                        <FiExternalLink size={13} />
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
              <Link to="/apply" className="fu-more">
                Open Apply <FiArrowRight size={13} />
              </Link>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
